import { useCallback } from 'react';
import { apiService } from '../services/api';

interface AppState {
  setSuccessMessage: (message: string) => void;
  clearSuccessMessage: () => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

interface UseDocumentUploadHandlersOptions {
  loadDocuments: () => Promise<void>;
  appState: AppState;
}

/**
 * useDocumentUploadHandlers - Shared upload logic for private and shared documents.
 *
 * Eliminates the duplicated upload handlers that existed in both
 * UnifiedSidebar's onUploadPrivateFile and ChatInputArea's onDocumentUpload.
 */
export function useDocumentUploadHandlers({
  loadDocuments,
  appState,
}: UseDocumentUploadHandlersOptions) {
  const handleUploadPrivate = useCallback(
    async (file: File) => {
      try {
        const response = await apiService.uploadDocument(file);
        if (response.document) {
          await loadDocuments();
          appState.setSuccessMessage(`Documento "${file.name}" subido correctamente`);
          setTimeout(() => appState.clearSuccessMessage(), 5000);
        }
      } catch (err: any) {
        if (err.message?.includes('Duplicate file') || err.message?.includes('already exists')) {
          const match = err.message.match(/already exists as "([^"]+)"/);
          const existingName = match ? match[1] : 'un documento existente';
          appState.setError(`El archivo "${file.name}" ya existe como "${existingName}".`);
          setTimeout(() => appState.clearError(), 8000);
        } else {
          console.error(`Error uploading ${file.name}:`, err);
          appState.setError(`Error al subir ${file.name}: ${err.message || 'Error desconocido'}`);
          setTimeout(() => appState.clearError(), 8000);
        }
      }
    },
    [loadDocuments, appState]
  );

  const handleUploadShared = useCallback(
    async (file: File) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiService.uploadAdminDocument(formData);
        if (response.success && response.document) {
          await loadDocuments();
          appState.setSuccessMessage(
            `Documento "${file.name}" subido a biblioteca compartida correctamente`
          );
          setTimeout(() => appState.clearSuccessMessage(), 5000);
        } else {
          throw new Error(response.message || 'Error al subir documento compartido');
        }
      } catch (err: any) {
        console.error(`Error uploading shared document ${file.name}:`, err);
        appState.setError(
          `Error al subir ${file.name} a biblioteca compartida: ${
            err.message || 'Error desconocido'
          }`
        );
        setTimeout(() => appState.clearError(), 8000);
      }
    },
    [loadDocuments, appState]
  );

  return { handleUploadPrivate, handleUploadShared };
}
